import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, forkJoin, map, of, switchMap } from 'rxjs';
import { LpsActions } from './lps.actions';
import { LpsService } from '@/core/services/lps.service';

@Injectable()
export class LpsEffects {
    actions$ = inject(Actions);
    lpsService = inject(LpsService);

    _loadDashboard = createEffect(() => {
        return this.actions$.pipe(
            ofType(LpsActions.loadDashboard),
            switchMap((action) => {
                return forkJoin({
                    activities: this.lpsService.getActivities(action.projectId),
                    constraints: this.lpsService.getConstraintsLog(action.projectId),
                    weeklyPlan: this.lpsService.getWeeklyPlan(action.projectId, action.year, action.weekNumber),
                    ppc: this.lpsService.getPpc(action.projectId, action.year, action.weekNumber),
                    milestones: this.lpsService.getMilestones(action.projectId)
                }).pipe(
                    map((data) => {
                        return LpsActions.loadDashboardSuccess({
                            activities: data.activities,
                            constraints: data.constraints,
                            weeklyPlan: data.weeklyPlan,
                            ppc: data.ppc,
                            milestones: data.milestones
                        });
                    }),
                    catchError((error) => {
                        console.error(error);
                        return of(LpsActions.loadDashboardFail({ error: error.message }));
                    })
                );
            })
        );
    });

    _loadActivities = createEffect(() => {
        return this.actions$.pipe(
            ofType(LpsActions.loadActivities),
            exhaustMap((action) => {
                return this.lpsService.getActivities(action.projectId, action.statusId, action.startDate, action.endDate).pipe(
                    map((activities) => LpsActions.loadActivitiesSuccess({ activities })),
                    catchError((error) => {
                        console.error(error);
                        return of(LpsActions.loadActivitiesFail({ error: error.message }));
                    })
                );
            })
        );
    });

    _createActivity = createEffect(() => {
        return this.actions$.pipe(
            ofType(LpsActions.createActivity),
            exhaustMap((action) => {
                return this.lpsService.createActivity(action.activity).pipe(
                    map((activity) => LpsActions.createActivitySuccess({ activity })),
                    catchError((error) => {
                        console.error(error);
                        return of();
                    })
                );
            })
        );
    });

    _updateActivity = createEffect(() => {
        return this.actions$.pipe(
            ofType(LpsActions.updateActivity),
            exhaustMap((action) => {
                return this.lpsService.updateActivity(action.id, action.activity).pipe(
                    map((activity) => LpsActions.updateActivitySuccess({ activity })),
                    catchError((error) => {
                        console.error(error);
                        return of();
                    })
                );
            })
        );
    });

    _loadConstraints = createEffect(() => {
        return this.actions$.pipe(
            ofType(LpsActions.loadConstraints),
            exhaustMap((action) => {
                return this.lpsService.getConstraintsLog(action.projectId).pipe(
                    map((constraints) => LpsActions.loadConstraintsSuccess({ constraints })),
                    catchError((error) => {
                        console.error(error);
                        return of(LpsActions.loadConstraintsFail({ error: error.message }));
                    })
                );
            })
        );
    });

    _addConstraint = createEffect(() => {
        return this.actions$.pipe(
            ofType(LpsActions.addConstraint),
            exhaustMap((action) => {
                return this.lpsService.addConstraint(action.constraint).pipe(
                    map((constraint) => LpsActions.addConstraintSuccess({ constraint })),
                    catchError((error) => {
                        console.error(error);
                        return of();
                    })
                );
            })
        );
    });

    _releaseConstraint = createEffect(() => {
        return this.actions$.pipe(
            ofType(LpsActions.releaseConstraint),
            exhaustMap((action) => {
                return this.lpsService.releaseConstraint(action.constraintId, action.userId).pipe(
                    map((constraint) => LpsActions.releaseConstraintSuccess({ constraint })),
                    catchError((error) => {
                        console.error(error);
                        return of();
                    })
                );
            })
        );
    });

    _loadWeeklyPlan = createEffect(() => {
        return this.actions$.pipe(
            ofType(LpsActions.loadWeeklyPlan),
            exhaustMap((action) => {
                return this.lpsService.getWeeklyPlan(action.projectId, action.year, action.weekNumber).pipe(
                    map((weeklyPlan) => LpsActions.loadWeeklyPlanSuccess({ weeklyPlan })),
                    catchError((error) => {
                        console.error(error);
                        return of(LpsActions.loadWeeklyPlanFail({ error: error.message }));
                    })
                );
            })
        );
    });

    _commitActivity = createEffect(() => {
        return this.actions$.pipe(
            ofType(LpsActions.commitActivity),
            exhaustMap((action) => {
                return this.lpsService.commitActivity(action.activityId, action.year, action.weekNumber, action.asCommitted, action.daysOfWeek).pipe(
                    map(() => LpsActions.commitActivitySuccess()),
                    catchError((error) => {
                        console.error(error);
                        return of();
                    })
                );
            })
        );
    });

    _loadPpc = createEffect(() => {
        return this.actions$.pipe(
            ofType(LpsActions.loadPpc),
            exhaustMap((action) => {
                return this.lpsService.getPpc(action.projectId, action.year, action.weekNumber).pipe(
                    map((ppc) => LpsActions.loadPpcSuccess({ ppc })),
                    catchError((error) => {
                        console.error(error);
                        return of();
                    })
                );
            })
        );
    });

    _loadCncAnalysis = createEffect(() => {
        return this.actions$.pipe(
            ofType(LpsActions.loadCncAnalysis),
            exhaustMap((action) => {
                return this.lpsService.getCncAnalysis(action.projectId, action.year, action.weekNumber).pipe(
                    map((cncAnalysis) => LpsActions.loadCncAnalysisSuccess({ cncAnalysis })),
                    catchError((error) => {
                        console.error(error);
                        return of();
                    })
                );
            })
        );
    });

    _loadMilestones = createEffect(() => {
        return this.actions$.pipe(
            ofType(LpsActions.loadMilestones),
            exhaustMap((action) => {
                return this.lpsService.getMilestones(action.projectId).pipe(
                    map((milestones) => LpsActions.loadMilestonesSuccess({ milestones })),
                    catchError((error) => {
                        console.error(error);
                        return of();
                    })
                );
            })
        );
    });
}
